import {
  useDebouncedSearch,
  useGetInvitesQuery,
  useGetUsersQuery,
  useWriteQueryParam,
} from '@consigli/hooks';
import { type Invite, type ProjectRole, type User } from '@consigli/types';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

/** Which of the two lists the tab body shows. */
export const PEOPLE_TAB = {
  USERS: 'users',
  INVITES: 'invites',
} as const;

export type PeopleTab = (typeof PEOPLE_TAB)[keyof typeof PEOPLE_TAB];

/**
 * A query param, so the Invites list is a link someone can send. The main tabs
 * are URL segments; a sub-tab is a param rather than a fourth segment because
 * the route table is built at module scope and each new segment costs a route.
 */
const TAB_PARAM = 'tab';

export interface UseProjectPeopleResult {
  tab: PeopleTab;
  setTab: (tab: PeopleTab) => void;
  /** Current, undebounced contents of the search box. */
  searchText: string;
  handleSearch: (event: ChangeEvent<HTMLInputElement>) => void;
  /** True between a keystroke and the debounced term reaching the API. */
  isSearchPending: boolean;
  /** The project's members, loaded whichever sub-tab is showing. */
  users: User[];
  /** Invites naming this project, loaded whichever sub-tab is showing. */
  invites: Invite[];
  /** True while the showing sub-tab's first (or newly searched) page resolves. */
  isLoading: boolean;
  isError: boolean;
  /** True while a retry is in flight, so the retry button can disable itself. */
  isRetrying: boolean;
  /** Retries whichever sub-tab failed. */
  refetch: () => void;
}

/**
 * The role a user holds *on this project*.
 *
 * Not `isOrgAdmin`, which is organisation-wide: reading that would badge someone
 * "Admin" on a project they belong to as a plain member. `projectAccess` is the
 * per-project grant, and the row for this project is the only one that answers
 * the column the frame draws.
 */
export const projectRoleOf = (user: User, projectId: number): ProjectRole | undefined =>
  user.projectAccess.find((access) => access.id === projectId)?.role;

/**
 * Loads the two people lists behind the Users & Invites tab: which sub-tab is
 * showing, the search term, and the rows for it.
 *
 * Both lists load on landing, whichever sub-tab the URL names, so switching
 * sub-tab shows rows at once rather than a fresh wait. The search box is shared,
 * so a search asks for both as well.
 *
 * `useGetUsersQuery`/`useGetInvitesQuery` directly rather than the `useUsers`
 * and `useInvites` wrappers, which expose no `refetch`, and there is no retry to
 * offer without it. The same reason project-overview-route.tsx reaches past
 * `useProject`.
 */
export const useProjectPeople = (projectId: number): UseProjectPeopleResult => {
  const [searchParams] = useSearchParams();
  const writeQueryParam = useWriteQueryParam();

  // Derived, not stored, and narrowed through PEOPLE_TAB because the URL is a
  // boundary anyone can type into: ?tab=nonsense shows Users rather than a body
  // with neither list in it.
  const tab: PeopleTab =
    searchParams.get(TAB_PARAM) === PEOPLE_TAB.INVITES ? PEOPLE_TAB.INVITES : PEOPLE_TAB.USERS;

  // Pushes, so back leaves the Invites list for the Users list rather than the
  // project list. Users is the default, so it clears the param instead of
  // spelling it out.
  const setTab = useCallback(
    (next: PeopleTab) => {
      writeQueryParam(TAB_PARAM, next === PEOPLE_TAB.INVITES ? next : '');
    },
    [writeQueryParam],
  );

  // Local, deliberately: the project list keeps its term in the URL so a
  // searched list can be shared, and pays for it with a two-way sync between the
  // param and the debounce. A membership search is a thing you do to find one
  // person on a page you already have open, so it is not worth that machinery.
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedSearch(searchText, 500);
  const isSearchPending = searchText !== debouncedSearch;

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const onUsers = tab === PEOPLE_TAB.USERS;

  const usersQuery = useGetUsersQuery({
    // 'all' rather than a page size: the frame draws no pagination control,
    // and a project's membership is tens of people. The organisation-wide
    // list on the user management page pages at 25 because it is the whole
    // tenant.
    page: 'all',
    // The filter `GET /accounts/` has always accepted and nothing in the
    // frontend used until now. It is the whole reason this list is the
    // project's members rather than the organisation's.
    projectIds: [projectId],
    search: debouncedSearch,
  });

  const invitesQuery = useGetInvitesQuery(
    // No project id: the endpoint takes none. See the filter below.
    { page: 'all', search: debouncedSearch },
  );

  /**
   * `GET /invites/` accepts page, page_size, status, search, recipient and
   * invited_by_email — no project id — so the project filter has to happen here.
   *
   * This is a stopgap and should not outlive the endpoint's next change. It asks
   * for every invite the caller is allowed to see and keeps the ones naming this
   * project: correct for a project with a handful of invites, wrong as a design,
   * and unable to page on the server. The server also decides what "allowed to
   * see" means, so a member who can read the project may still be shown fewer
   * invites than exist on it — which is the argument for the real filter rather
   * than something to paper over here.
   */
  const invites = useMemo(
    () =>
      (invitesQuery.data?.results ?? []).filter((invite) =>
        invite.projects?.some((project) => project.id === projectId),
      ),
    [invitesQuery.data, projectId],
  );

  const showing = onUsers ? usersQuery : invitesQuery;

  return {
    tab,
    setTab,
    searchText,
    handleSearch,
    isSearchPending,
    users: usersQuery.data?.results ?? [],
    invites,
    // `isLoading` and not `isFetching`: it covers the first load and every new
    // search term, because a changed term is a new cache key, but stays false
    // while a cached list is revalidated — which would otherwise replace a list
    // the user is reading with placeholders.
    isLoading: showing.isLoading,
    isError: showing.isError,
    isRetrying: showing.isFetching,
    refetch: useCallback(() => void showing.refetch(), [showing]),
  };
};
